import { DEFECT_TYPES_NAMES } from '../live-interface/verallia-interface-service';

export const VERALLIA_DASHBOARD_TIME_ZONE = 'Europe/Paris';

export const VERALLIA_DASHBOARD_DEFECTS = Object.entries(DEFECT_TYPES_NAMES).map(([id, name]) => ({
  id: Number(id),
  name,
}));

export const VERALLIA_DASHBOARD_OTHER_DEFECT = {
  id: 'other_defect',
  name: 'DEFECTO DESCONOCIDO',
};

const VERALLIA_DASHBOARD_SHIFTS = {
  1: { id: 1, startHour: 7, endHour: 15 },
  2: { id: 2, startHour: 15, endHour: 23 },
  3: { id: 3, startHour: 23, endHour: 7 },
};

const roundToOneDecimal = (value) => Math.round(value * 10) / 10;

const getZonedDateParts = (timestamp) => {
  const formatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: VERALLIA_DASHBOARD_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });

  const parts = formatter.formatToParts(new Date(timestamp * 1000)).reduce((result, part) => {
    if (part.type !== 'literal') result[part.type] = Number(part.value);
    return result;
  }, {});

  return {
    year: parts.year,
    month: parts.month,
    day: parts.day,
    hour: parts.hour,
    minute: parts.minute,
    second: parts.second,
  };
};

const zonedDateTimeToTimestamp = ({ year, month, day, hour = 0, minute = 0, second = 0 }) => {
  const targetUtc = Date.UTC(year, month - 1, day, hour, minute, second);
  let guess = targetUtc;

  for (let index = 0; index < 3; index++) {
    const parts = getZonedDateParts(Math.floor(guess / 1000));
    const representedUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);

    guess += targetUtc - representedUtc;
  }

  return Math.floor(guess / 1000);
};

const addCalendarDays = ({ year, month, day }, amount) => {
  const date = new Date(Date.UTC(year, month - 1, day + amount));

  return {
    year: date.getUTCFullYear(),
    month: date.getUTCMonth() + 1,
    day: date.getUTCDate(),
  };
};

const getCalendarDatesInRange = (start, end) => {
  const startParts = getZonedDateParts(start);
  const endParts = getZonedDateParts(end);
  const startDate = Date.UTC(startParts.year, startParts.month - 1, startParts.day);
  const endDate = Date.UTC(endParts.year, endParts.month - 1, endParts.day);
  const dates = [];

  for (let cursor = startDate; cursor <= endDate; cursor += 86400000) {
    const date = new Date(cursor);

    dates.push({
      year: date.getUTCFullYear(),
      month: date.getUTCMonth() + 1,
      day: date.getUTCDate(),
    });
  }

  return dates;
};

const getIsoWeek = ({ year, month, day }) => {
  const date = new Date(Date.UTC(year, month - 1, day));
  const dayNumber = date.getUTCDay() || 7;

  date.setUTCDate(date.getUTCDate() + 4 - dayNumber);

  const isoYear = date.getUTCFullYear();
  const yearStart = new Date(Date.UTC(isoYear, 0, 1));
  const week = Math.ceil((((date - yearStart) / 86400000) + 1) / 7);

  return { year: isoYear, week };
};

const getShiftRangeFromDate = (date, shiftId) => {
  const normalizedShiftId = Number(shiftId);
  const shift = VERALLIA_DASHBOARD_SHIFTS[normalizedShiftId];
  const endDate = normalizedShiftId === 3 ? addCalendarDays(date, 1) : date;

  return {
    id: normalizedShiftId,
    start: zonedDateTimeToTimestamp({ ...date, hour: shift.startHour }),
    end: zonedDateTimeToTimestamp({ ...endDate, hour: shift.endHour }),
  };
};

const getValidSizes = (detections) => {
  return detections.map(({ size }) => size).filter((size) => Number.isFinite(size));
};

const getAverageSize = (detections) => {
  const sizes = getValidSizes(detections);
  if (sizes.length === 0) return null;

  return sizes.reduce((sum, size) => sum + size, 0) / sizes.length;
};

const getDefectGroupId = (defectId) => {
  if (defectId === null || defectId === undefined || defectId === '') return VERALLIA_DASHBOARD_OTHER_DEFECT.id;

  const normalizedDefectId = Number(defectId);

  return VERALLIA_DASHBOARD_DEFECTS.some(({ id }) => id === normalizedDefectId)
    ? normalizedDefectId
    : VERALLIA_DASHBOARD_OTHER_DEFECT.id;
};

export const getVeralliaDashboardDefectDefinition = (defectId) => {
  const groupedDefectId = getDefectGroupId(defectId);

  return VERALLIA_DASHBOARD_DEFECTS.find(({ id }) => id === groupedDefectId) ?? VERALLIA_DASHBOARD_OTHER_DEFECT;
};

export const getVeralliaDashboardDefectLabel = (defectId) => {
  return getVeralliaDashboardDefectDefinition(defectId).name;
};

export const getVeralliaDashboardAvailableLines = (detections) => {
  return [...new Set(detections.map(({ linea }) => linea).filter(Boolean))]
    .sort((lineA, lineB) => lineA.localeCompare(lineB, undefined, { numeric: true }));
};

export const getVeralliaDashboardAvailableDefects = (detections) => {
  const availableIds = new Set(detections.map(({ defect }) => getDefectGroupId(defect)));
  const availableDefects = VERALLIA_DASHBOARD_DEFECTS.filter(({ id }) => availableIds.has(id));

  if (availableIds.has(VERALLIA_DASHBOARD_OTHER_DEFECT.id)) availableDefects.push(VERALLIA_DASHBOARD_OTHER_DEFECT);

  return availableDefects;
};

export const getVeralliaDashboardPeriodRange = (rangeKey, detections, nowTimestamp = Math.floor(Date.now() / 1000)) => {
  if (rangeKey === 'last_7d') {
    return {
      start: nowTimestamp - 7 * 86400,
      end: nowTimestamp,
      rangeKey,
    };
  }

  if (rangeKey === 'last_30d') {
    return {
      start: nowTimestamp - 30 * 86400,
      end: nowTimestamp,
      rangeKey,
    };
  }

  if (rangeKey === 'all') {
    return {
      start: detections.length > 0 ? Math.min(...detections.map(({ timestamp }) => timestamp)) : nowTimestamp - 86400,
      end: nowTimestamp,
      rangeKey,
    };
  }

  return {
    start: nowTimestamp - 86400,
    end: nowTimestamp,
    rangeKey: 'last_24h',
  };
};

export const getVeralliaDashboardDefaultFilters = (detections) => {
  return {
    lines: [],
    defects: [],
    ...getVeralliaDashboardPeriodRange('last_7d', detections),
  };
};

export const filterVeralliaDashboardDetections = (detections, filters) => {
  return detections.filter((detection) => {
    const matchesLine = filters.lines.length === 0 || filters.lines.includes(detection.linea);
    const matchesDefect = filters.defects.length === 0 || filters.defects.includes(getDefectGroupId(detection.defect));
    const matchesPeriod = detection.timestamp >= filters.start && detection.timestamp <= filters.end;

    return matchesLine && matchesDefect && matchesPeriod;
  });
};

export const sortVeralliaDashboardDetections = (detections) => {
  return [...detections].sort((a, b) => b.timestamp - a.timestamp);
};

export const getVeralliaDashboardShift = (timestamp) => {
  const { hour } = getZonedDateParts(timestamp);

  if (hour >= 7 && hour < 15) return 1;
  if (hour >= 15 && hour < 23) return 2;

  return 3;
};

export const getVeralliaDashboardShiftRange = (timestamp) => {
  const parts = getZonedDateParts(timestamp);
  const shiftId = getVeralliaDashboardShift(timestamp);

  let date = {
    year: parts.year,
    month: parts.month,
    day: parts.day,
  };

  if (shiftId === 3 && parts.hour < 7) date = addCalendarDays(date, -1);

  return getShiftRangeFromDate(date, shiftId);
};

const getVeralliaProductionShiftRange = (timestamp) => {
  const parts = getZonedDateParts(timestamp);
  const shiftId = parts.hour >= 6 && parts.hour < 14 ? 1 : parts.hour >= 14 && parts.hour < 22 ? 2 : 3;
  const shiftHours = { 1: [6, 14], 2: [14, 22], 3: [22, 6] };
  const [startHour, endHour] = shiftHours[shiftId];
  let date = { year: parts.year, month: parts.month, day: parts.day };
  if (shiftId === 3 && parts.hour < 6) date = addCalendarDays(date, -1);
  const endDate = shiftId === 3 ? addCalendarDays(date, 1) : date;

  return {
    id: shiftId,
    start: zonedDateTimeToTimestamp({ ...date, hour: startHour }),
    end: zonedDateTimeToTimestamp({ ...endDate, hour: endHour }),
  };
};

export const getVeralliaDashboardLastCompletedShiftRange = (nowTimestamp = Math.floor(Date.now() / 1000)) => {
  const currentShift = getVeralliaProductionShiftRange(nowTimestamp);
  return getVeralliaProductionShiftRange(currentShift.start - 1);
};

export const formatVeralliaDashboardDateTime = (timestamp, withSeconds = true) => {
  const parts = getZonedDateParts(timestamp);

  const day = String(parts.day).padStart(2, '0');
  const month = String(parts.month).padStart(2, '0');
  const year = parts.year;
  const hour = String(parts.hour).padStart(2, '0');
  const minute = String(parts.minute).padStart(2, '0');
  const second = String(parts.second).padStart(2, '0');

  return `${day}/${month}/${year} ${hour}:${minute}${withSeconds ? `:${second}` : ''}`;
};

export const formatVeralliaDashboardSize = (size) => {
  if (!Number.isFinite(size)) return '—';
  return `${size.toFixed(1)} cm`;
};

export const getVeralliaDashboardDefectClassification = (detections) => {
  if (detections.length === 0) return [];

  const groupedDetections = detections.reduce((groups, detection) => {
    const defectId = getDefectGroupId(detection.defect);

    if (!groups[defectId]) groups[defectId] = [];
    groups[defectId].push(detection);

    return groups;
  }, {});

  return Object.entries(groupedDetections)
    .map(([defectId, defectDetections]) => {
      const normalizedDefectId = defectId === VERALLIA_DASHBOARD_OTHER_DEFECT.id ? defectId : Number(defectId);

      return {
        id: normalizedDefectId,
        name: getVeralliaDashboardDefectLabel(normalizedDefectId),
        count: defectDetections.length,
        share: roundToOneDecimal(defectDetections.length / detections.length * 100),
        averageSize: getAverageSize(defectDetections),
      };
    })
    .sort((a, b) => b.count - a.count);
};

export const getVeralliaDashboardDefectCatalogue = (detections) => {
  const unknownDetections = detections.filter(({ defect }) => getDefectGroupId(defect) === VERALLIA_DASHBOARD_OTHER_DEFECT.id);
  const definitions = [
    ...VERALLIA_DASHBOARD_DEFECTS,
    ...(unknownDetections.length > 0 ? [VERALLIA_DASHBOARD_OTHER_DEFECT] : []),
  ];

  return definitions
    .map((definition) => {
      const defectDetections = definition.id === VERALLIA_DASHBOARD_OTHER_DEFECT.id
        ? unknownDetections
        : detections.filter(({ defect }) => getDefectGroupId(defect) === definition.id);

      const sizes = getValidSizes(defectDetections);
      const count = defectDetections.length;

      return {
        ...definition,
        count,
        share: detections.length > 0 ? roundToOneDecimal(count / detections.length * 100) : 0,
        averageSize: sizes.length > 0 ? sizes.reduce((sum, size) => sum + size, 0) / sizes.length : null,
        maxSize: sizes.length > 0 ? Math.max(...sizes) : null,
        linesAffected: new Set(defectDetections.map(({ linea }) => linea).filter(Boolean)).size,
        lastSeen: defectDetections.length > 0 ? Math.max(...defectDetections.map(({ timestamp }) => timestamp)) : null,
      };
    })
    .sort((a, b) => b.count - a.count);
};

export const getVeralliaDashboardLinesReporting = (detections) => {
  return new Set(detections.map(({ linea }) => linea).filter(Boolean)).size;
};

export const getVeralliaDashboardByHour = (detections, start, end) => {
  const counts = Array(24).fill(0);
  const occurrences = Array(24).fill(0);

  detections.forEach(({ timestamp }) => {
    const { hour } = getZonedDateParts(timestamp);
    counts[hour] += 1;
  });

  let cursor = Math.floor(start / 3600) * 3600;

  while (cursor <= end) {
    const { hour } = getZonedDateParts(cursor);
    occurrences[hour] += 1;
    cursor += 3600;
  }

  return {
    labels: Array.from({ length: 24 }, (_, hour) => `${String(hour).padStart(2, '0')}:00`),
    values: counts.map((count, hour) => occurrences[hour] > 0 ? count / occurrences[hour] : 0),
  };
};

export const getVeralliaDashboardByDay = (detections, start, end) => {
  const counts = Array(31).fill(0);
  const occurrences = Array(31).fill(0);

  detections.forEach(({ timestamp }) => {
    const { day } = getZonedDateParts(timestamp);
    counts[day - 1] += 1;
  });

  getCalendarDatesInRange(start, end).forEach(({ day }) => {
    occurrences[day - 1] += 1;
  });

  return {
    labels: Array.from({ length: 31 }, (_, index) => String(index + 1)),
    values: counts.map((count, index) => occurrences[index] > 0 ? roundToOneDecimal(count / occurrences[index]) : 0),
  };
};

export const getVeralliaDashboardByWeek = (detections, start, end) => {
  const counts = {};
  const occurrences = {};
  const seenWeeks = new Set();

  detections.forEach(({ timestamp }) => {
    const dateParts = getZonedDateParts(timestamp);
    const { week } = getIsoWeek(dateParts);

    counts[week] = (counts[week] ?? 0) + 1;
  });

  getCalendarDatesInRange(start, end).forEach((date) => {
    const { year, week } = getIsoWeek(date);
    const instanceKey = `${year}-${week}`;

    if (seenWeeks.has(instanceKey)) return;

    seenWeeks.add(instanceKey);
    occurrences[week] = (occurrences[week] ?? 0) + 1;
  });

  const weeks = Object.keys(occurrences).map(Number).sort((a, b) => a - b);

  return {
    labels: weeks.map((week) => `S${week}`),
    values: weeks.map((week) => roundToOneDecimal((counts[week] ?? 0) / occurrences[week])),
  };
};

export const getVeralliaDashboardByMonth = (detections, start, end) => {
  const counts = Array(12).fill(0);
  const occurrences = Array(12).fill(0);
  const seenMonths = new Set();

  detections.forEach(({ timestamp }) => {
    const { month } = getZonedDateParts(timestamp);
    counts[month - 1] += 1;
  });

  getCalendarDatesInRange(start, end).forEach(({ year, month }) => {
    const instanceKey = `${year}-${month}`;

    if (seenMonths.has(instanceKey)) return;

    seenMonths.add(instanceKey);
    occurrences[month - 1] += 1;
  });

  return {
    labels: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'],
    values: counts.map((count, index) => occurrences[index] > 0 ? roundToOneDecimal(count / occurrences[index]) : 0),
  };
};

export const getVeralliaDashboardByShift = (detections, start, end) => {
  const counts = { 1: 0, 2: 0, 3: 0 };
  const occurrences = { 1: 0, 2: 0, 3: 0 };
  const seenShiftInstances = new Set();
  const dates = getCalendarDatesInRange(start, end);

  if (dates.length > 0) dates.unshift(addCalendarDays(dates[0], -1));

  dates.forEach((date) => {
    [1, 2, 3].forEach((shiftId) => {
      const shiftRange = getShiftRangeFromDate(date, shiftId);

      if (shiftRange.end < start || shiftRange.start > end) return;

      const instanceKey = `${shiftRange.start}-${shiftId}`;
      if (seenShiftInstances.has(instanceKey)) return;

      seenShiftInstances.add(instanceKey);
      occurrences[shiftId] += 1;
    });
  });

  detections.forEach(({ shift }) => {
    if ([1, 2, 3].includes(shift)) counts[shift] += 1;
  });

  return {
    labels: ['1', '2', '3'],
    values: [1, 2, 3].map((shiftId) => {
      return occurrences[shiftId] > 0 ? roundToOneDecimal(counts[shiftId] / occurrences[shiftId]) : 0;
    }),
  };
};

export const getVeralliaDashboardByLine = (detections) => {
  const lines = getVeralliaDashboardAvailableLines(detections);

  return {
    labels: lines,
    values: lines.map((linea) => detections.filter((detection) => detection.linea === linea).length),
  };
};

export const getVeralliaDashboardShiftEvolution = (detections, shiftRange) => {
  const bucketDuration = 30 * 60;
  const bucketCount = Math.ceil((shiftRange.end - shiftRange.start) / bucketDuration);
  const hourly = Array(bucketCount).fill(0);

  detections.forEach(({ timestamp }) => {
    const bucketIndex = Math.floor((timestamp - shiftRange.start) / bucketDuration);
    if (bucketIndex >= 0 && bucketIndex < bucketCount) hourly[bucketIndex] += 1;
  });

  const labels = Array.from({ length: bucketCount }, (_, index) => {
    const timestamp = shiftRange.start + index * bucketDuration;
    const parts = getZonedDateParts(timestamp);

    return `${String(parts.hour).padStart(2, '0')}:${String(parts.minute).padStart(2, '0')}`;
  });

  let cumulativeCount = 0;

  const cumulative = hourly.map((count) => {
    cumulativeCount += count;
    return cumulativeCount;
  });

  return { labels, hourly, cumulative };
};